import BasicRecord from "../utils/BasicRecord";
import { isAlmostEqual } from "../utils/math";
import Color from "./color";
import Model from "./model";
import TextStyleDefinition from "./TextStyleDefinition";
import TextDecoration from "./TextDecoration";
import { TextTransform, VerticalAlignment } from "../api/model/textStyles/TextStyle";
import { VariableReference } from "../api/model/variables/Variables";

const REGISTERED_AXES = Object.freeze({
    WEIGHT: "wght",
    WIDTH: "wdth",
    SLANT: "slnt",
    ITALIC: "ital",
    OPTICAL_SIZING: "opsz"
});

const weightRegex =
    /(thin|hairline|([ds]emi|(ex|ul)tra)?(light|bold)|normal|regular|book|medium|heavy|black|[1-9]\d{2}$)/ig;
const styleRegex = /(italic|oblique)/ig;
const stretchRegex = /((?:(ultra|extra|semi)[\s-]?)?(?:condensed|expanded))/ig;

function removeLastOccurence(str: string, text: string) {
    const lastIndex = str.lastIndexOf(text);
    return str.substring(0, lastIndex) + str.substr(lastIndex + text.length);
}

// Reference: https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight#Common_weight_name_mapping
const WEIGHT_REPRESENTATION: Record<number, string> = Object.freeze({
    100: "thin",
    200: "extra light",
    300: "light",
    400: "regular",
    500: "medium",
    600: "semi bold",
    700: "bold",
    800: "extra bold",
    900: "black",
    950: "extra black"
});

const WEIGHT_VALUE = Object.freeze({
    THIN: 100,
    EXTRA_LIGHT: 200,
    LIGHT: 300,
    REGULAR: 400,
    MEDIUM: 500,
    SEMI_BOLD: 600,
    BOLD: 700,
    EXTRA_BOLD: 800,
    BLACK: 900,
    EXTRA_BLACK: 950
});

function getWeightValue(weight: string): number {
    if (/black|heavy|900/gi.test(weight)) {
        return WEIGHT_VALUE.BLACK;
    } else if (/((ex|ul)tra)\s?bold|800/gi.test(weight)) {
        return WEIGHT_VALUE.EXTRA_BOLD;
    } else if (/([ds]emi)\s?bold|600/gi.test(weight)) {
        return WEIGHT_VALUE.SEMI_BOLD;
    } else if (/bold|700/gi.test(weight)) {
        return WEIGHT_VALUE.BOLD;
    } else if (/medium|500/gi.test(weight)) {
        return WEIGHT_VALUE.MEDIUM;
    } else if (/((ex|ul)tra)\s?light|200/gi.test(weight)) {
        return WEIGHT_VALUE.EXTRA_LIGHT;
    } else if (/light|300/gi.test(weight)) {
        return WEIGHT_VALUE.LIGHT;
    } else if (/thin|hairline|100/gi.test(weight)) {
        return WEIGHT_VALUE.THIN;
    }
    return WEIGHT_VALUE.REGULAR;
}

// Reference: https://developer.mozilla.org/en-US/docs/Web/CSS/font-stretch#Keyword_to_numeric_mapping
const STRETCH_REPRESENTATION: Record<number, string> = Object.freeze({
    50: "ultra condensed",
    62.5: "extra condensed",
    75: "condensed",
    87.5: "semi condensed",
    100: "normal",
    112.5: "semi expanded",
    125: "expanded",
    150: "extra expanded",
    200: "ultra expanded"
});

const STRETCH_VALUE = Object.freeze({
    ULTRA_CONDENSED: 50,
    EXTRA_CONDENSED: 62.5,
    CONDENSED: 75,
    SEMI_CONDENSED: 87.5,
    NORMAL: 100,
    SEMI_EXPANDED: 112.5,
    EXPANDED: 125,
    EXTRA_EXPANDED: 150,
    ULTRA_EXPANDED: 200
});

function getStretchValue(stretch: string): number {
    if (/(ultra(\s|-)?)+condensed/i.test(stretch)) {
        return STRETCH_VALUE.ULTRA_CONDENSED;
    } else if (/(extra(\s|-)?)+condensed/i.test(stretch)) {
        return STRETCH_VALUE.EXTRA_CONDENSED;
    } else if (/(semi(\s|-)?)+condensed/i.test(stretch)) {
        return STRETCH_VALUE.SEMI_CONDENSED;
    } else if (/condensed/i.test(stretch)) {
        return STRETCH_VALUE.CONDENSED;
    } else if (/(ultra(\s|-)?)+expanded/i.test(stretch)) {
        return STRETCH_VALUE.ULTRA_EXPANDED;
    } else if (/(extra(\s|-)?)+expanded/i.test(stretch)) {
        return STRETCH_VALUE.EXTRA_EXPANDED;
    } else if (/(semi(\s|-)?)+expanded/i.test(stretch)) {
        return STRETCH_VALUE.SEMI_EXPANDED;
    } else if (/expanded/i.test(stretch)) {
        return STRETCH_VALUE.EXPANDED;
    }
    return STRETCH_VALUE.NORMAL;
}

function applyFontExceptions(fontFace: string): string {
    /**
     * Arial font family might have MT in it
     */
    if (/^Arial/.test(fontFace)) {
        return fontFace.replace("MT", "");
    }

    /**
     * Lato font family defines Thin as 200, and Heavy as 800
     */
    if (/^Lato/.test(fontFace)) {
        return fontFace.replace(/thin/i, WEIGHT_VALUE.EXTRA_LIGHT.toString())
            .replace(/heavy/i, WEIGHT_VALUE.EXTRA_BOLD.toString());
    }

    return fontFace;
}

function normalizeFontFamily(fontFamily: string): string {
    return fontFamily.replace(/-+/g, "-") // Replace remaining multiple dashes with single dashes
        .replace(/-$/, "") // Remove any trailing dashes
        .trim(); // Remove any leading and trailing spaces left
}

function getFontParts(fontFace: string): {
    family: string;
    style: string;
    weight: number;
    stretch: number;
} {
    let fontFamily = applyFontExceptions(fontFace);

    const weightMatches = fontFamily.match(weightRegex);
    let weight: number;
    if (!weightMatches) {
        weight = WEIGHT_VALUE.REGULAR;
    } else {
        const lastWeightMatch = weightMatches[weightMatches.length - 1];
        fontFamily = removeLastOccurence(fontFamily, lastWeightMatch);
        weight = getWeightValue(lastWeightMatch.toLowerCase());
    }

    const styleMatches = fontFamily.match(styleRegex);
    let style: string;
    if (!styleMatches) {
        style = "normal";
    } else {
        style = styleMatches[styleMatches.length - 1];
        fontFamily = removeLastOccurence(fontFamily, style);
        style = style.toLowerCase();
    }

    const stretchMatches = fontFamily.match(stretchRegex);
    let stretch: number;
    if (!stretchMatches) {
        stretch = STRETCH_VALUE.NORMAL;
    } else {
        const lastStretchMatch = stretchMatches[stretchMatches.length - 1];
        fontFamily = removeLastOccurence(fontFamily, lastStretchMatch);
        stretch = getStretchValue(lastStretchMatch.toLowerCase());
    }

    const family = normalizeFontFamily(fontFamily);

    return {
        family,
        style,
        weight,
        stretch
    };
}

function convertFontVariationSettings(fontVariationSettings: BasicRecord<number>): {
    style: string;
    weight: number;
    stretch: number;
} {
    const weight = REGISTERED_AXES.WEIGHT in fontVariationSettings
        ? fontVariationSettings[REGISTERED_AXES.WEIGHT]
        : WEIGHT_VALUE.REGULAR;

    const stretch = REGISTERED_AXES.WIDTH in fontVariationSettings
        ? fontVariationSettings[REGISTERED_AXES.WIDTH]
        : STRETCH_VALUE.NORMAL;

    let style: string;
    if (REGISTERED_AXES.SLANT in fontVariationSettings) {
        style = `oblique ${fontVariationSettings[REGISTERED_AXES.SLANT]}deg`;
    } else if (fontVariationSettings[REGISTERED_AXES.ITALIC]) {
        style = "italic";
    } else {
        style = "normal";
    }

    return {
        style,
        weight,
        stretch
    };
}

class Font extends Model {
    id?: string;
    name: string;
    fontFace: string;
    fontSize: number;
    sourceId?: string;
    letterSpacing?: number;
    lineHeight?: number;
    color?: Color;
    textAlign?: string;
    fontVariationSettings?: BasicRecord<number>;
    fontFamily: string;
    fontStyle: string;
    fontWeight: number;
    fontStretch: number;
    scaledFontSize?: number;
    paragraphSpacing?: number;
    textTransform?: TextTransform;
    textDecoration?: TextDecoration;
    verticalAlignment?: VerticalAlignment;
    listSpacing?: number;
    paragraphIndent?: number;
    variableReferences?: VariableReference[];

    constructor(fontData: TextStyleDefinition) {
        super();

        const {
            _id,
            id = _id,
            sourceId,
            name,
            fontFace,
            fontSize,
            lineHeight,
            textAlign,
            letterSpacing,
            fontVariationSettings,
            color,
            paragraphSpacing,
            textTransform,
            textDecoration,
            verticalAlignment,
            listSpacing,
            paragraphIndent,
            variableReferences
        } = fontData;

        this.id = _id || id;
        this.sourceId = sourceId;
        this.name = name;
        this.variableReferences = variableReferences;

        this.fontFace = fontFace;
        this.fontSize = fontSize;
        this.lineHeight = lineHeight;
        this.textAlign = textAlign;
        this.letterSpacing = letterSpacing;
        this.color = color && new Color(color);
        this.paragraphSpacing = paragraphSpacing;
        this.verticalAlignment = verticalAlignment;
        this.listSpacing = listSpacing;
        this.paragraphIndent = paragraphIndent;
        this.textTransform = textTransform;
        if (textDecoration) {
            this.textDecoration = new TextDecoration(textDecoration);
        }

        let family;
        let style;
        let weight;
        let stretch;

        if (fontVariationSettings) {
            this.fontVariationSettings = fontVariationSettings;
            family = fontFace;
            ({ style, weight, stretch } = convertFontVariationSettings(fontVariationSettings));
        } else {
            ({ family, style, weight, stretch } = getFontParts(fontFace));
        }

        this.fontFamily = family;
        this.fontStyle = style;
        this.fontWeight = weight;
        this.fontStretch = stretch;
    }

    get weightText(): string {
        return WEIGHT_REPRESENTATION[this.fontWeight] ?? String(this.fontWeight);
    }

    get stretchText(): string {
        return STRETCH_REPRESENTATION[this.fontStretch] ?? String(this.fontStretch);
    }

    override toObject(): TextStyleDefinition {
        const fontObj: TextStyleDefinition = {
            name: this.name,
            fontFace: this.fontFace,
            fontSize: this.fontSize,
            lineHeight: this.lineHeight,
            textAlign: this.textAlign,
            letterSpacing: this.letterSpacing,
            paragraphSpacing: this.paragraphSpacing,
            verticalAlignment: this.verticalAlignment,
            listSpacing: this.listSpacing,
            paragraphIndent: this.paragraphIndent,
            textTransform: this.textTransform,
            textDecoration: this.textDecoration?.toObject(),
            sourceId: this.sourceId,
            variableReferences: this.variableReferences
        };

        if (this.fontVariationSettings) {
            fontObj.fontVariationSettings = this.fontVariationSettings;
        }

        if (this.color) {
            fontObj.color = this.color.toObject();
        }

        return fontObj;
    }

    isFontVariationSettingsEqual(fvs: BasicRecord<number>): boolean {
        if (Object.keys(this.fontVariationSettings!).length !== Object.keys(fvs).length) {
            return false;
        }

        return Object.entries(this.fontVariationSettings!).every(([axisName, value]) =>
            isAlmostEqual(fvs[axisName], value)
        );
    }

    // eslint-disable-next-line complexity
    equals(f: Font): boolean {
        const baseEquality = this.fontFamily === f.fontFamily &&
            isAlmostEqual(this.fontSize, f.fontSize) &&
            isAlmostEqual(this.lineHeight, f.lineHeight) &&
            isAlmostEqual(this.letterSpacing, f.letterSpacing) &&
            isAlmostEqual(this.paragraphSpacing, f.paragraphSpacing) &&
            isAlmostEqual(this.paragraphIndent, f.paragraphIndent) &&
            isAlmostEqual(this.listSpacing, f.listSpacing) &&
            this.verticalAlignment === f.verticalAlignment &&
            this.textDecoration?.strikethrough === f.textDecoration?.strikethrough &&
            this.textDecoration?.underline === f.textDecoration?.underline &&
            this.textTransform === f.textTransform &&
            this.textAlign === f.textAlign &&
            (!this.color || !f.color || this.color.equals(f.color));

        let variationEquality;
        if (this.fontVariationSettings && f.fontVariationSettings) {
            variationEquality = this.isFontVariationSettingsEqual(f.fontVariationSettings);
        } else if (this.fontVariationSettings || f.fontVariationSettings) {
            return false;
        } else {
            variationEquality = this.fontStyle === f.fontStyle &&
                this.fontStretch === f.fontStretch &&
                this.fontWeight === f.fontWeight;
        }

        return baseEquality && variationEquality;
    }

    conforms(f: Font, densityScale: number, styleguideDensityScale: number): boolean {
        const baseConformance = this.fontFamily === f.fontFamily &&
            isAlmostEqual(this.fontSize / densityScale, f.fontSize / styleguideDensityScale) &&
            (this.textAlign === f.textAlign || !f.textAlign) &&
            (!f.lineHeight ||
                isAlmostEqual(this.lineHeight && this.lineHeight / densityScale, f.lineHeight / styleguideDensityScale)
            ) &&
            (!f.letterSpacing ||
                isAlmostEqual(
                    this.letterSpacing && this.letterSpacing / densityScale,
                    f.letterSpacing / styleguideDensityScale)
            ) &&
            (!this.color || !f.color || this.color.equals(f.color));

        let variationConformance;
        if (this.fontVariationSettings && f.fontVariationSettings) {
            variationConformance = this.isFontVariationSettingsEqual(f.fontVariationSettings);
        } else if (this.fontVariationSettings || f.fontVariationSettings) {
            return false;
        } else {
            variationConformance = this.fontStyle === f.fontStyle &&
                this.fontStretch === f.fontStretch &&
                this.fontWeight === f.fontWeight;
        }

        return baseConformance && variationConformance;
    }
}

export default Font;
